import React, { useMemo } from "react";
import { Card, CardContent, Typography, Divider, Grid, useMediaQuery, useTheme } from "@mui/material";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { useFormatAmount } from "src/business-types/laundry/modules/home/hooks/useFormatAmount";

export interface PaymentDistributionChartProps {
  title?: string;
  paymentMethods: Record<string, number>;
  height?: number | string;
  colors?: string[];
  showLegend?: boolean;
  showTotal?: boolean;
  compact?: boolean;
}

const DEFAULT_COLORS = ["#4f46e5", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#84cc16", "#f97316"];

const PaymentDistributionChart: React.FC<PaymentDistributionChartProps> = ({
  title = "Payment Distribution",
  paymentMethods,
  height = 300,
  colors = DEFAULT_COLORS,
  showLegend = true,
  showTotal = true,
  compact = false,
}) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const FormatAmount = useFormatAmount()

  // ✅ Transform payment method data into chart-friendly format
  const data = useMemo(
    () =>
      Object.entries(paymentMethods)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value),
    [paymentMethods]
  );

  // ✅ Calculate total number of payments
  const total = useMemo(() => Object.values(paymentMethods).reduce((a, b) => a + b, 0), [paymentMethods]);

  const getColor = (name: string, i: number) => (name.toLowerCase().includes("cancel") ? "#ef4444" : colors[i % colors.length]);

  const getPercent = (v: number) => (total ? ((v / total) * 100).toFixed(1) : "0.0");

  // ✅ Custom tooltip for the chart
  const TooltipBox = ({ active, payload }: any) =>
    active && payload?.length ? (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
        <p className="font-semibold text-gray-900">{payload[0].name}</p>
        <p className="text-sm text-gray-600">
           {FormatAmount(payload[0].value)} payments ({getPercent(payload[0].value)}%)
        </p>
      </div>
    ) : null;

  return (
    <Card className="w-full bg-white rounded-xl shadow-md border border-gray-100">
      <CardContent className="p-4 sm:p-6">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-2 mb-4">
          <Typography variant="h6" className="font-bold text-gray-900">
            {title}
          </Typography>

          {showTotal && total > 0 && <div className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold">Total: {total} payments</div>}
        </div>

        <Divider className="mb-4" />

        {/* ✅ Empty State */}
        {!data.length ? (
          <div className="flex flex-col items-center py-12 text-gray-500">
            <div className="text-4xl mb-3">📊</div>
            <Typography>No payment data available</Typography>
            <Typography variant="body2" className="text-gray-500 mt-1">
              Payment distribution will appear here
            </Typography>
          </div>
        ) : (
          <Grid container spacing={3} alignItems="center">
            {/* Pie Chart */}
            <Grid item xs={12} md={showLegend ? 7 : 12}>
              <div style={{ height: compact ? 250 : height }} className="relative">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={data} innerRadius={compact ? 50 : 60} outerRadius={compact ? 80 : 90} paddingAngle={2} dataKey="value">
                      {data.map((d, i) => (
                        <Cell key={d.name} fill={getColor(d.name, i)} stroke="#fff" strokeWidth={2} />
                      ))}
                    </Pie>
                    <Tooltip content={<TooltipBox />} />
                  </PieChart>
                </ResponsiveContainer>

                {!compact && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <div className="text-2xl font-bold">{FormatAmount(total)}</div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">Total</div>
                  </div>
                )}
              </div>
            </Grid>

            {/* Legend */}
            {showLegend && (
              <Grid item xs={12} md={5}>
                <div className={`space-y-3 ${isSmall ? "text-center" : "text-left"} overflow-y-auto max-h-[280px] pr-1`}>
                  {data.map((item, i) => {
                    const color = getColor(item.name, i);
                    const percent = getPercent(item.value);
                    const isCancelled = item.name.toLowerCase().includes("cancel");

                    return (
                      <div
                        key={item.name}
                        className={`flex items-center gap-3 p-2 rounded-lg transition-all duration-200
                          ${isSmall ? "justify-center" : "justify-start"}
                          ${isCancelled ? "bg-red-50 border border-red-100" : "hover:bg-gray-50"}
                        `}
                      >
                        <div className="w-4 h-4 rounded-full" style={{ background: color }} />
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between text-sm font-semibold">
                            <span className={isCancelled ? "text-red-800" : "text-gray-700"}>{item.name}</span>
                            <span className={`text-xs px-2 py-1 rounded-full ${isCancelled ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-700"}`}>{percent}%</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-gray-500">{FormatAmount(item.value)}</span>
                            <div className="h-1 bg-gray-200 rounded-full w-16">
                              <div
                                className="h-full rounded-full"
                                style={{
                                  width: `${percent}%`,
                                  background: color,
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Grid>
            )}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentDistributionChart;
